import React from 'react';
import { LocalAudioTrack, LocalVideoTrack, Participant, RemoteAudioTrack, RemoteVideoTrack } from 'twilio-video';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import { styled } from '@mui/material/styles';

import AudioLevelIndicator from '../../AudioLevelIndicator/AudioLevelIndicator';
import PinIcon from '../PinIcon/PinIcon';
import ScreenShareIcon from '../../../icons/ScreenShareIcon';
import VideoOffSmallIcon from '../../../icons/VideoOffSmallIcon';
import VideoOnSmallIcon from '../../../icons/VideoOnSmallIcon';

import useIsTrackSwitchedOff from '../../../hooks/useIsTrackSwitchedOff/useIsTrackSwitchedOff';
import usePublications from '../../../hooks/usePublications/usePublications';
import useTrack from '../../../hooks/useTrack/useTrack';
import useParticipantIsReconnecting from '../../../hooks/useParticipantIsReconnecting/useParticipantIsReconnecting';

import { getIdentityInitials, removeIdentityMasking } from '../../../utils';

const AvatarContainer = styled('span')(({ theme }) => ({
  width: '45px',
  height: '45px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#191b1d',
  color: 'white',
  fontSize: 'medium',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    '& svg': {
      transform: 'scale(0.7)',
    },
  },
}));

const ReconnectingContainer = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  alignItems: 'end',
  justifyContent: 'center',
  background: 'rgba(40, 42, 43, 0.75)',
  zIndex: 1,
});

const ScreenShareIconContainer = styled('span')({
  background: 'white',
  width: '22px',
  height: '28px',
  display: 'flex',
  '& path': {
    fill: '#10416D',
  },
  '& rect': {
    fill: '#10416D',
  }
});

const IdentityType = styled('span')({
  background: '#FFA85C',
  color: 'white',
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  borderRadius: '10px',
  justifyContent: 'center',
  minWidth: '36px',
  maxHeight: '19px',
  fontSize: '0.7rem'
});



const TypographySecondary = styled(Typography)({
  color: '#10416D',
  fontSize: '1rem',
});

const ActionContainer = styled(ListItemSecondaryAction)({
  display: 'flex',
  maxHeight: '28px',
  overflow: 'hidden',
  alignItems: 'center'
});

const VideoIconContainer = styled('span')({
  height: '28px',
  '& .videoOn *': {
    fill: '#10416D'
  }
});

interface ParticipantInfoProps {
  participant: Participant;
  onClick?: () => void;
  isLocalParticipant?: boolean;
  isSelected?: boolean;
}

export default function ParticipantInfo({
  participant,
  onClick,
  isLocalParticipant,
  isSelected
}: ParticipantInfoProps) {
  const publications = usePublications(participant);

  const audioPublication = publications.find(p => p.kind === 'audio');
  const videoPublication = publications.find(p => !p.trackName.includes('screen') && p.kind === 'video');

  const isVideoEnabled = Boolean(videoPublication);
  const videoTrack = useTrack(videoPublication);
  const isVideoSwitchedOff = useIsTrackSwitchedOff(videoTrack as LocalVideoTrack | RemoteVideoTrack);

  const isScreenShareEnabled = publications.find(p => p.trackName.includes('screen'));

  const audioTrack = useTrack(audioPublication) as LocalAudioTrack | RemoteAudioTrack | undefined;
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);

  return (
    <ListItem >
      <ListItemAvatar>
        <AvatarContainer>
          {getIdentityInitials(participant.identity)}
        </AvatarContainer>
      </ListItemAvatar>
      <ListItemText
        primary={<Typography
          variant="body1"
          component="span"
          sx={{
            color: '#10416D',
            fontSize: '0.7rem',
            maxWidth: '50%',
          }}
        >
          {removeIdentityMasking(participant.identity)}
        </Typography>}
        secondary={isParticipantReconnecting ? (
          <ReconnectingContainer>
            <TypographySecondary variant="body1">
              Reconnecting...
            </TypographySecondary>
          </ReconnectingContainer>
        ) : ""}
      />
      <ActionContainer>
        {isLocalParticipant && <IdentityType> ME </IdentityType>}
        <AudioLevelIndicator audioTrack={audioTrack} color="#10416D" />
        <VideoIconContainer>
          {(!isVideoEnabled || isVideoSwitchedOff) ? <VideoOffSmallIcon /> : <VideoOnSmallIcon className="videoOn" />}
        </VideoIconContainer>
        {isSelected && <PinIcon />}
        {isScreenShareEnabled && (
          <ScreenShareIconContainer>
            <ScreenShareIcon />
          </ScreenShareIconContainer>
        )}
      </ActionContainer>
    </ListItem>
  );
}
